import { AreaDeTexto, Casilla, Fecha, Json, Numero, Radio, Seleccion, Texto } from './campos/Controles'
import { Bloques, FilaDeMaquetacion, Filas, Grupo, Pestanas, Plegable } from './campos/Compuestos'
import { ControlEspecial, hayControlEspecial } from './campos/Especiales'
import { Medio } from './campos/Medio'
import { Relacion } from './campos/Relacion'
import { TextoRico } from './campos/TextoRico'
import type { Campo } from './esquema'
import { useFormulario } from './Formulario'
import { CampoVinculable, esMapaDeVinculos, useBloqueDePlantilla } from '../estructura/Vinculos'

/**
 * De un campo del esquema a su control. Es el único sitio que sabe qué tipo se pinta
 * con qué; añadir un tipo de campo es añadir una línea aquí.
 *
 * Un campo que no pasa su condición no se pinta: lo dice el núcleo, que tiene las
 * funciones (`useCondiciones`), no una copia de la regla aquí.
 */
export const CampoGenerico = ({ campo, ruta }: { campo: Campo; ruta: string }) => {
  const { ocultos } = useFormulario()
  const enPlantilla = useBloqueDePlantilla()
  if (campo.condicionado && ocultos.has(ruta)) return null
  // El mapa de vínculos de un bloque de plantilla no se pinta como JSON: lo escriben los «Vincular a…» de cada campo.
  if (esMapaDeVinculos(campo)) return null
  // Dentro de un bloque de plantilla, cada campo puede tomar un dato del documento (estructura/Vinculos.tsx).
  if (enPlantilla) {
    return (
      <CampoVinculable campo={campo} ruta={ruta}>
        <Control campo={campo} ruta={ruta} />
      </CampoVinculable>
    )
  }
  return <Control campo={campo} ruta={ruta} />
}

const Control = ({ campo, ruta }: { campo: Campo; ruta: string }) => {
  const { soloLectura: todoSoloLectura } = useFormulario()
  const soloLectura = todoSoloLectura || Boolean(campo.soloLectura)

  // Un campo con un control propio (lo pide el núcleo): la clave que sale de la etiqueta, un desplegable de campos…
  if (hayControlEspecial(campo)) return <ControlEspecial campo={campo} ruta={ruta} soloLectura={soloLectura} />

  switch (campo.tipo) {
    case 'text':
    case 'email':
    case 'password':
      return <Texto campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'textarea':
      return <AreaDeTexto campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'number':
      return <Numero campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'checkbox':
      return <Casilla campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'date':
      return <Fecha campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'select':
      return <Seleccion campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'radio':
      return <Radio campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'json':
    case 'code':
      return <Json campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'relationship':
      return <Relacion campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'upload':
      return <Medio campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'richText':
      return <TextoRico campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'group':
      return <Grupo campo={campo} ruta={ruta} />
    case 'row':
      return <FilaDeMaquetacion campo={campo} ruta={ruta} />
    case 'collapsible':
      return <Plegable campo={campo} ruta={ruta} />
    case 'tabs':
      return <Pestanas campo={campo} ruta={ruta} />
    case 'array':
      return <Filas campo={campo} ruta={ruta} soloLectura={soloLectura} />
    case 'blocks':
      return <Bloques campo={campo} ruta={ruta} soloLectura={soloLectura} />
    default:
      return (
        <p className="text-xs text-tenue">
          {campo.etiqueta}: este tipo de campo ({campo.tipo}) todavía no se puede editar aquí.
        </p>
      )
  }
}
